import { useNavigate, useSearchParams } from "react-router-dom";
import styles from "./ListeningAudio.module.less";
import CommonNavBar from "views/components/CommonNavBar";
import CommonModal from "views/components/CommonModal";
import { useState, useRef, useEffect } from "react";
import DSBridge from "dsbridge";
import { getAudioCategoryList, getAudioList } from "api/picture";
import { Toast } from "react-vant";

const ListeningAudio = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isDragging, setIsDragging] = useState(false);
  const [progress, setProgress] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [audioAvailable, setAudioAvailable] = useState(false);
  const [showList, setShowList] = useState(false);
  const [categories, setCategories] = useState([]);
  const [audioList, setAudioList] = useState([]);
  const [activeCategory, setActiveCategory] = useState("");
  const [currentAudioId, setCurrentAudioId] = useState("");
  const [pageMap, setPageMap] = useState({});
  const [hasMoreMap, setHasMoreMap] = useState({});
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [lockShow, setLockShow] = useState(false);
  const [loopMode, setLoopMode] = useState(
    () => localStorage.getItem("utalk_listening_audio_loop_mode") || "list",
  ); // "list" | "single"
  const progressBoxRef = useRef(null);
  const buttonRef = useRef(null);
  const touchOffsetRef = useRef(0);
  const audioRef = useRef(null);
  const LoadingRef = useRef(null);
  const shouldAutoPlayRef = useRef(false);

  // 格式化时间
  const formatTime = (time) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes.toString().padStart(2, "0")}:${seconds
      .toString()
      .padStart(2, "0")}`;
  };

  // 计算按钮和进度条的实际像素位置
  const calculatePixelPositions = () => {
    if (!buttonRef.current || !progressBoxRef.current) {
      return { buttonLeft: "0px", progressWidth: "0px" };
    }

    const buttonWidth = buttonRef.current.getBoundingClientRect().width;
    const containerWidth = progressBoxRef.current.getBoundingClientRect().width;
    const maxPosition = containerWidth - buttonWidth;

    let pixelPosition;
    if (progress <= 0) {
      pixelPosition = 0;
    } else if (progress >= 100) {
      pixelPosition = maxPosition;
    } else {
      pixelPosition = (progress / 100) * maxPosition;
    }

    return {
      buttonLeft: `${pixelPosition}px`,
      progressWidth: `${pixelPosition + buttonWidth / 2}px`,
    };
  };

  const calculateProgress = (touch) => {
    if (!progressBoxRef.current || !buttonRef.current) return;

    const rect = progressBoxRef.current.getBoundingClientRect();
    const buttonRect = buttonRef.current.getBoundingClientRect();
    const buttonWidth = buttonRect.width;
    const maxPosition = rect.width - buttonWidth;

    // 计算触摸点相对于进度条的位置，并考虑偏移量
    const touchX = touch.clientX - rect.left - touchOffsetRef.current;

    // 限制在有效范围内
    const boundedOffsetX = Math.max(0, Math.min(maxPosition, touchX));
    const newProgress = (boundedOffsetX / maxPosition) * 100;

    return Math.max(0, Math.min(100, newProgress));
  };

  const handleTouchStart = (e) => {
    if (!audioAvailable) return;
    if (!progressBoxRef.current || !buttonRef.current) return;

    const touch = e.touches[0];
    const rect = progressBoxRef.current.getBoundingClientRect();
    const buttonRect = buttonRef.current.getBoundingClientRect();
    const buttonWidth = buttonRect.width;

    // 计算触摸点相对于进度条的位置
    const touchX = touch.clientX - rect.left;
    // 计算按钮中心点相对于进度条的位置
    const buttonX = buttonRect.left - rect.left + buttonWidth / 2;

    // 如果点击位置在按钮附近，设置偏移量并开始拖动
    if (Math.abs(touchX - buttonX) < buttonWidth) {
      touchOffsetRef.current = touchX - buttonX;
      setIsDragging(true);
    } else {
      // 如果点击位置不在按钮附近，直接设置新的进度
      const maxPosition = rect.width - buttonWidth;
      const boundedOffsetX = Math.max(0, Math.min(maxPosition, touchX));
      const newProgress = (boundedOffsetX / maxPosition) * 100;
      setProgress(newProgress);
      if (audioRef.current) {
        audioRef.current.currentTime = (newProgress / 100) * duration;
      }
    }
  };

  const handleTouchMove = (e) => {
    if (isDragging) {
      e.preventDefault();
      const newProgress = calculateProgress(e.touches[0]);
      if (newProgress !== undefined) {
        setProgress(newProgress);
      }
    }
  };

  const handleTouchEnd = () => {
    if (isDragging && audioRef.current) {
      audioRef.current.currentTime = (progress / 100) * duration;
    }
    setIsDragging(false);
    touchOffsetRef.current = 0;
  };

  const togglePlay = () => {
    const currentAudio = audioList.find(
      (item) => item.audio_id === currentAudioId,
    );
    if (currentAudio && currentAudio.is_lock === 1) {
      setLockShow(currentAudio.wechat_link || true);
      return;
    }

    if (!audioAvailable) {
      Toast.fail("当前音频未加载成功");
      return;
    }
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  // 初始化音频
  const initAudio = (audioUrl, autoPlay = false) => {
    if (!audioRef.current || !audioUrl) return;

    if (LoadingRef.current?.clear) {
      LoadingRef.current.clear();
    }
    Toast.clear();

    shouldAutoPlayRef.current = autoPlay;
    audioRef.current.src = audioUrl;
    LoadingRef.current = Toast.loading({
      message: "正在加载音频...",
      forbidClick: true,
      duration: 0,
    });
  };

  useEffect(() => {
    const progressBox = progressBoxRef.current;
    if (!progressBox) return;

    progressBox.addEventListener("touchmove", handleTouchMove, {
      passive: false,
    });
    progressBox.addEventListener("touchend", handleTouchEnd);
    progressBox.addEventListener("touchcancel", handleTouchEnd);

    return () => {
      progressBox.removeEventListener("touchmove", handleTouchMove);
      progressBox.removeEventListener("touchend", handleTouchEnd);
      progressBox.removeEventListener("touchcancel", handleTouchEnd);
    };
  }, [isDragging, progress, duration]);

  // 新增一个单独的 useEffect 来处理 timeupdate
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !audioAvailable) return;

    const handleTimeUpdate = () => {
      const currentAudio = audioRef.current;
      if (!currentAudio) return;

      setCurrentTime(currentAudio.currentTime);
      if (!isDragging) {
        setProgress((currentAudio.currentTime / currentAudio.duration) * 100);
      }
    };

    audio.addEventListener("timeupdate", handleTimeUpdate);

    return () => {
      audio.removeEventListener("timeupdate", handleTimeUpdate);
    };
  }, [audioAvailable, isDragging]); // 添加必要的依赖项

  // 检查环境
  useEffect(() => {
    return () => {
      if (LoadingRef.current?.clear) {
        LoadingRef.current.clear();
      }
    };
  }, []);

  const handleTabChange = async (cat) => {
    if (activeCategory === cat) return;
    setActiveCategory(cat);

    // 如果当前类别的数据已经存在，就不重新请求
    if (audioList.some((item) => item.category === cat)) {
      return;
    }

    if (LoadingRef.current?.clear) {
      LoadingRef.current.clear();
    }
    Toast.clear();
    const tabLoading = Toast.loading({
      message: "正在加载...",
      forbidClick: true,
      duration: 0,
    });
    try {
      const res = await getAudioList({ page: 1, limit: 1000, category: cat });
      if (res?.code === 200 && res.data?.list) {
        setPageMap((prev) => ({ ...prev, [cat]: 1 }));
        setHasMoreMap((prev) => ({
          ...prev,
          [cat]: res.data.pagination?.has_next,
        }));
        // 将新请求到的数据拼接到原有的 audioList 中，而不是替换
        setAudioList((prev) => {
          // 过滤掉没有 category 的坏数据
          const newList = (res.data.list || []).filter((item) => item.category);
          // 过滤掉可能重复的数据，保留其他类别的数据并加入新类别的数据
          const prevFiltered = prev.filter((item) => item.category !== cat);
          console.log([...prevFiltered, ...newList]);
          return [...prevFiltered, ...newList];
        });
      } else {
        Toast.fail(res?.message || "列表加载失败");
      }
    } catch (err) {
      Toast.fail("列表加载失败");
    } finally {
      if (tabLoading && tabLoading.clear) {
        tabLoading.clear();
      }
    }
  };

  const loadMoreAudio = async (specificCategory) => {
    const cat = specificCategory || activeCategory;
    if (isLoadingMore || !hasMoreMap[cat]) return null;

    setIsLoadingMore(true);
    const loadingToast = Toast.loading({
      message: "加载下一页...",
      forbidClick: false,
      duration: 0,
    });
    const nextPage = (pageMap[cat] || 1) + 1;

    try {
      const res = await getAudioList({ page: nextPage, limit: 1000, category: cat });
      if (res?.code === 200 && res.data?.list) {
        setPageMap((prev) => ({ ...prev, [cat]: nextPage }));
        setHasMoreMap((prev) => ({
          ...prev,
          [cat]: res.data.pagination?.has_next,
        }));

        const newList = (res.data.list || []).filter((item) => item.category);
        setAudioList((prev) => [...prev, ...newList]);
        return newList;
      }
    } catch (err) {
      Toast.fail("加载更多失败");
    } finally {
      if (loadingToast && loadingToast.clear) {
        loadingToast.clear();
      }
      setIsLoadingMore(false);
    }
    return null;
  };

  const handleListScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    // 当滚动到底部距离不足 50px 时加载更多
    if (scrollHeight - scrollTop - clientHeight < 50) {
      loadMoreAudio();
    }
  };

  const handleSelectAudio = (item, autoPlay = false) => {
    setCurrentAudioId(item.audio_id);
    if (audioRef.current) {
      audioRef.current.pause();
      setIsPlaying(false);
      setProgress(0);
      setCurrentTime(0);
    }

    if (item.is_lock === 1) {
      setLockShow(item.wechat_link || true);
      setAudioAvailable(false);
      return;
    }

    if (item.audio_url) {
      initAudio(item.audio_url, autoPlay);
    } else {
      setAudioAvailable(false);
      Toast.fail("暂无音频地址");
    }
  };

  const handlePrev = () => {
    if (!audioList.length) return;
    const currentAudio = audioList.find(
      (item) => item.audio_id === currentAudioId,
    );
    if (!currentAudio) return;

    const currentCategoryList = audioList.filter(
      (item) => item.category === currentAudio.category,
    );
    const currentIndex = currentCategoryList.findIndex(
      (item) => item.audio_id === currentAudioId,
    );

    let prevIndex = currentIndex - 1;
    if (prevIndex < 0) {
      prevIndex = currentCategoryList.length - 1;
    }

    const prevAudio = currentCategoryList[prevIndex];
    handleSelectAudio(prevAudio, isPlaying || loopMode === "list");
  };

  const handleNext = async (isEnded = false) => {
    if (!audioList.length) return;
    const currentAudio = audioList.find(
      (item) => item.audio_id === currentAudioId,
    );
    if (!currentAudio) return;

    const currentCategoryList = audioList.filter(
      (item) => item.category === currentAudio.category,
    );
    const currentIndex = currentCategoryList.findIndex(
      (item) => item.audio_id === currentAudioId,
    );

    let nextIndex = currentIndex + 1;
    let nextAudio = null;

    if (nextIndex >= currentCategoryList.length) {
      if (hasMoreMap[currentAudio.category]) {
        // 请求下一页数据
        const newList = await loadMoreAudio(currentAudio.category);
        if (newList && newList.length > 0) {
          nextAudio = newList[0];
        }
      }

      if (!nextAudio) {
        // 如果没有更多或加载失败，回到当前分类的第一个
        nextIndex = 0;
        nextAudio = currentCategoryList[nextIndex];
      }
    } else {
      nextAudio = currentCategoryList[nextIndex];
    }

    if (nextAudio) {
      handleSelectAudio(nextAudio, isEnded || isPlaying);
    }
  };

  const toggleLoopMode = () => {
    const newMode = loopMode === "list" ? "single" : "list";
    setLoopMode(newMode);
    localStorage.setItem("utalk_listening_audio_loop_mode", newMode);
    Toast.info(newMode === "list" ? "列表循环播放" : "单曲循环播放");
  };

  const handleClickWx = () => {
    if (!lockShow || typeof lockShow !== "string") {
      return Toast.info("暂无跳转链接，请稍后重试");
    }
    try {
      if (DSBridge.hasNativeMethod("route.navigation")) {
        DSBridge.call(
          "route.navigation",
          {
            route: "miniProgram",
            mid: "gh_ce82ce6b1629",
            path: lockShow,
          },
          (back) => {
            console.log(back);
          }
        );
      }
    } catch (e) {
      console.log(e);
    }
    setLockShow(false);
  };

  // 记录上次播放的音频 ID 和分类
  useEffect(() => {
    if (currentAudioId && activeCategory) {
      localStorage.setItem("utalk_listening_audio_last_id", currentAudioId);
      localStorage.setItem("utalk_listening_audio_last_category", activeCategory);
    }
  }, [currentAudioId, activeCategory]);

  // 分开一个 useEffect 处理音频初始化
  useEffect(() => {
    const initData = async () => {
      try {
        const categoryRes = await getAudioCategoryList();
        if (categoryRes?.code === 200 && categoryRes.data) {
          const categories = categoryRes.data || [];
          setCategories(categories);

          if (categories.length > 0) {
            let targetCategory = categories[0];
            const id = Number(searchParams.get("id"));
            const lastPlayedId = Number(localStorage.getItem("utalk_listening_audio_last_id"));
            const lastPlayedCat = localStorage.getItem("utalk_listening_audio_last_category");

            // 如果没有URL传参，并且上次播放的分类存在于当前列表中，优先加载上次分类
            if (!id && lastPlayedCat && categories.includes(lastPlayedCat)) {
              targetCategory = lastPlayedCat;
            }

            setActiveCategory(targetCategory);

            const listRes = await getAudioList({
              page: 1,
              limit: 1000,
              category: targetCategory,
            });

            if (listRes?.code === 200 && listRes.data?.list) {
              setPageMap((prev) => ({ ...prev, [targetCategory]: 1 }));
              setHasMoreMap((prev) => ({
                ...prev,
                [targetCategory]: listRes.data.pagination?.has_next,
              }));
              const initialList = listRes.data.list.filter((item) => item.category);
              setAudioList(initialList);

              let currentAudio = null;
              if (id) {
                currentAudio = initialList.find((item) => item.audio_id === id);
              }
              // 如果没有URL指定或找不到，尝试匹配上次播放的ID
              if (!currentAudio && lastPlayedId) {
                currentAudio = initialList.find((item) => item.audio_id === lastPlayedId);
              }
              // 兜底第一项
              if (!currentAudio && initialList.length > 0) {
                currentAudio = initialList[0];
              }

              if (currentAudio) {
                setCurrentAudioId(currentAudio.audio_id);
                if (currentAudio.is_lock === 1) {
                  setLockShow(currentAudio.wechat_link || true);
                  setAudioAvailable(false);
                } else if (currentAudio.audio_url) {
                  initAudio(currentAudio.audio_url);
                } else {
                  setAudioAvailable(false);
                  Toast.fail("暂无音频地址，请退出重试");
                }
              } else {
                setAudioAvailable(false);
                Toast.fail("未找到相关音频");
              }
            } else {
              setAudioAvailable(false);
              Toast.fail(listRes?.message || "音频加载失败，请稍后重试");
            }
          } else {
            setAudioAvailable(false);
            Toast.fail("暂无音频级别");
          }
        } else {
          setAudioAvailable(false);
          Toast.fail(categoryRes?.message || "获取音频级别失败，请稍后重试");
        }
      } catch (err) {
        setAudioAvailable(false);
        Toast.fail("音频加载失败，请稍后重试");
      }
    };

    initData();

    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.removeAttribute("src");
        audioRef.current.load();
        setAudioAvailable(false);
      }
      Toast.clear();
    };
  }, [searchParams]);

  // 监听 showList、activeCategory 和 currentAudioId 变化，滚动到当前选中的音频位置
  useEffect(() => {
    if (showList) {
      // 延迟等待 DOM 渲染并显示
      const timer = setTimeout(() => {
        const activeElement = document.querySelector(
          `.${styles["list-item-active"]}`,
        );
        const listContainer = document.querySelector(
          `.${styles["popup-list"]}`,
        );
        
        if (activeElement && listContainer) {
          // 使用 scrollTop 计算来替代 scrollIntoView，兼容性更好
          const containerRect = listContainer.getBoundingClientRect();
          const elementRect = activeElement.getBoundingClientRect();
          
          // 计算需要滚动到的目标位置，使元素居中
          // 当前滚动位置 + 元素相对容器的偏移 - 容器一半的高度 + 元素一半的高度
          const targetScrollTop = listContainer.scrollTop + 
            (elementRect.top - containerRect.top) - 
            (containerRect.height / 2) + 
            (elementRect.height / 2);
            
          // 兼容旧版设备的滚动方式
          if (typeof listContainer.scrollTo === 'function') {
            listContainer.scrollTo({
              top: targetScrollTop,
              behavior: "smooth"
            });
          } else {
            // 最原始的兜底方案
            listContainer.scrollTop = targetScrollTop;
          }
        }
      }, 150); // 稍微增加一点延迟确保样式和布局计算已完成
      return () => clearTimeout(timer);
    }
  }, [showList, activeCategory, currentAudioId]);

  return (
    <div className={styles["audio-preview-container"]}>
      <CommonModal isOpen={!!lockShow}>
        <div className={styles["pop-container--lock"]}>
          <div className={styles["lock-wrapper"]}>
            <div className={styles["lock-wrapper-title"]}>温馨提示</div>
            <div className={styles["lock-wrapper-text"]}>
              您没有当前音频权限，请联系助教老师，开通权益哦！
            </div>
            <div
              className={styles["lock-confirm"]}
              onClick={handleClickWx}
            >
              联系助教老师
            </div>
          </div>
          <img
            onClick={() => setLockShow(false)}
            className={styles["lock-close"]}
            src={require("@/images/mine/recommend-officer/button_cancel.png")}
            alt="close"
          />
        </div>
      </CommonModal>

      <>
        <audio
          ref={audioRef}
            loop={loopMode === "single"}
            onLoadedMetadata={() => {
              if (audioRef.current) {
                setDuration(audioRef.current.duration);
                setAudioAvailable(true);
                if (LoadingRef.current?.clear) {
                  LoadingRef.current.clear();
                }
                if (shouldAutoPlayRef.current) {
                  audioRef.current
                    .play()
                    .then(() => {
                      setIsPlaying(true);
                    })
                    .catch(() => {
                      setIsPlaying(false);
                    });
                  shouldAutoPlayRef.current = false;
                }
              }
            }}
            onError={(e) => {
              const audio = audioRef.current;
              if (audio) {
                const currentSrc = audio.currentSrc || audio.src;
                // 忽略没有 src 或 src 被解析为当前页面 URL（比如 src="" 或无 src 时的默认行为）时的报错
                if (!currentSrc || currentSrc === window.location.href) {
                  return;
                }
                // 忽略被主动终止的情况（比如在加载过程中切换音频）
                if (audio.error && audio.error.code === 1) {
                  return;
                }
              }

              setAudioAvailable(false);
              setIsPlaying(false);
              Toast.fail("音频加载失败，请稍后重试");
              if (LoadingRef.current?.clear) {
                LoadingRef.current.clear();
              }
            }}
            onEnded={() => {
              setProgress(0);
              setCurrentTime(0);
              setIsPlaying(false);
              if (loopMode === "list") {
                handleNext(true);
              }
            }}
          />
          <div className={styles["player-container"]}>
            <img
              className={styles["right-banner"]}
              src={require("@/images/login/right-banner.png")}
            />
            <CommonNavBar
              backgroundColor={"transparent"}
              onClickBack={() => {
                Toast.clear();
                navigate(-1);
              }}
              title="磨耳音频"
            />
            <div className={styles["scroll-content"]}>
              <img
                className={styles["audio-pic"]}
                src={require("@/images/audio-preview/audio-pic-2.jpg")}
              />
              <div className={styles["audio-name"]}>
                {audioList.find((item) => item.audio_id === currentAudioId)
                  ?.alias || ""}
              </div>
              <div className={styles["audio-text"]}>
                <div>Listen to the audio and read aloud!</div>
                <div style={{ textAlign: "left" }}>边听音频边大声朗读哦！</div>
              </div>
              <>
                <div
                  className={styles["progress-box"]}
                  ref={progressBoxRef}
                  onTouchStart={handleTouchStart}
                >
                  <div
                    className={styles["current-progress"]}
                    style={{ width: calculatePixelPositions().progressWidth }}
                  ></div>
                  <div
                    className={styles["current-button"]}
                    ref={buttonRef}
                    style={{ left: calculatePixelPositions().buttonLeft }}
                  ></div>
                </div>
                <div className={styles["audio-time"]}>
                  {formatTime(currentTime)}/{formatTime(duration)}
                </div>
                <div className={styles["button-list"]}>
                  <img
                    className={styles["common-button"]}
                    src={require(
                      `@/images/audio-preview/cycle-${
                        loopMode === "list" ? "1" : "2"
                      }.png`,
                    )}
                    onClick={toggleLoopMode}
                  />
                  <div className={styles["center-list"]}>
                    <img
                      className={styles["common-button"]}
                      src={require("@/images/audio-preview/audio-prev.png")}
                      onClick={() => handlePrev()}
                    />
                    <img
                      className={styles["play-button"]}
                      src={require(
                        isPlaying
                          ? "@/images/audio-preview/audio-pause.png"
                          : "@/images/audio-preview/audio-play.png",
                      )}
                      onClick={togglePlay}
                    />
                    <img
                      className={styles["common-button"]}
                      src={require("@/images/audio-preview/audio-next.png")}
                      onClick={() => handleNext(false)}
                    />
                  </div>
                  <img
                    className={styles["common-button"]}
                    src={require("@/images/audio-preview/list-img.png")}
                    onClick={() => setShowList(true)}
                  />
                </div>
              </>
            </div>
          </div>
          {showList && (
            <div
              className={styles["custom-popup-overlay"]}
              onClick={() => setShowList(false)}
            >
              <div
                className={styles["custom-popup-content"]}
                onClick={(e) => e.stopPropagation()}
              >
                <div className={styles["popup-header"]}>
                  <div className={styles["popup-title"]}>播放列表</div>
                  <img
                    className={styles["popup-close"]}
                    src={require("@/images/audio-preview/close-img.png")}
                    onClick={() => setShowList(false)}
                  />
                </div>
                <div className={styles["popup-tabs"]}>
                  {categories.map((cat) => (
                    <div
                      key={cat}
                      className={`${styles["tab-item"]} ${
                        activeCategory === cat ? styles["tab-item-active"] : ""
                      }`}
                      onClick={() => handleTabChange(cat)}
                    >
                      {cat}
                    </div>
                  ))}
                </div>
                <div className={styles["popup-list"]} onScroll={handleListScroll}>
                  {audioList
                    .filter((item) => item.category === activeCategory)
                    .map((item) => (
                      <div
                        key={item.audio_id}
                        className={`${styles["list-item"]} ${
                          currentAudioId === item.audio_id
                            ? styles["list-item-active"]
                            : ""
                        }`}
                        onClick={() => handleSelectAudio(item, true)}
                      >
                        <div className={styles["item-name"]}>{item.alias}</div>
                        {currentAudioId === item.audio_id && (
                          <img
                            className={styles["item-select-icon"]}
                            src={require("@/images/audio-preview/select-img.png")}
                          />
                        )}
                      </div>
                    ))}
                </div>
              </div>
            </div>
          )}
        </>
    </div>
  );
};

export default ListeningAudio;
